
import { useState } from 'react';
import { BusinessPhoto } from '../../../../mocks/directoryDetails';

interface PhotoGalleryProps {
  photos: BusinessPhoto[];
  businessName: string;
}

export default function PhotoGallery({
  photos,
  businessName,
}: PhotoGalleryProps) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  // Guard against empty photo arrays
  if (!photos?.length) return null;

  const featured = photos[0];
  const rest = photos.slice(1, 5);

  // Helper to safely get a photo (prevents out‑of‑bounds errors)
  const getPhoto = (idx: number) => photos[idx] ?? null;

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-camera-line text-teal-600"></i>
          </div>
          Photos
        </h3>

        <div className="grid grid-cols-4 gap-2">
          {/* Featured large photo */}
          <button
            onClick={() => setLightboxIdx(0)}
            className="col-span-2 row-span-2 relative rounded-xl overflow-hidden group cursor-pointer"
          >
            <div className="w-full h-64">
              <img
                src={featured.url}
                alt={`${businessName} - ${featured.alt}`}
                title={`${businessName} ${featured.alt}`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="absolute inset-0 bg-black\\/0 group-hover:bg-black\\/20 transition-colors flex items-center justify-center">
              <i className="ri-zoom-in-line text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </div>
          </button>

          {/* Smaller photos */}
          {rest.map((photo, idx) => (
            <button
              key={photo.id}
              onClick={() => setLightboxIdx(idx + 1)}
              className="relative rounded-xl overflow-hidden group cursor-pointer"
            >
              <div className="w-full h-[124px]">
                <img
                  src={photo.url}
                  alt={`${businessName} - ${photo.alt}`}
                  title={`${businessName} ${photo.alt}`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-black\\/0 group-hover:bg-black\\/20 transition-colors flex items-center justify-center">
                <i className="ri-zoom-in-line text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
              </div>
              {idx === rest.length - 1 && photos.length > 5 && (
                <div className="absolute inset-0 bg-black\\/50 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    +{photos.length - 5} more
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-black\\/90 flex items-center justify-center p-4"
          onClick={() => setLightboxIdx(null)}
        >
          <button
            onClick={() => setLightboxIdx(null)}
            className="absolute top-6 right-6 w-10 h-10 bg-white\\/10 hover:bg-white\\/20 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {lightboxIdx > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIdx(lightboxIdx - 1);
              }}
              className="absolute left-6 w-10 h-10 bg-white\\/10 hover:bg-white\\/20 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
          )}

          {lightboxIdx < photos.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIdx(lightboxIdx + 1);
              }}
              className="absolute right-6 w-10 h-10 bg-white\\/10 hover:bg-white\\/20 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          )}

          <div
            className="max-w-4xl max-h-[80vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const photo = getPhoto(lightboxIdx);
              if (!photo) return null;
              return (
                <>
                  <img
                    src={photo.url}
                    alt={photo.alt}
                    className="w-full h-full object-contain rounded-lg"
                  />
                  <p className="text-white/70 text-sm text-center mt-3">
                    {photo.alt} — {lightboxIdx + 1} of {photos.length}
                  </p>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}
